<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmojiIcon from 'dashboard/components-next/emoji-icon-picker/EmojiIcon.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import TeamDrawer from './TeamDrawer.vue';
import { useSettingsRowMenu } from '../components/useSettingsRowMenu';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();
const route = useRoute();
const router = useRouter();
// R2-9-6: new/edit are routes that open the team drawer over this list (same instance, meta.keepAliveKey).
const drawerRef = ref(null);
const drawerTeamId = computed(() =>
  route.name === 'settings_teams_edit' ? route.params.teamId : null
);
const isDrawerOpen = computed(() =>
  ['settings_teams_new', 'settings_teams_edit'].includes(route.name)
);
const openInDrawer = to =>
  drawerRef.value
    ? drawerRef.value.runGuarded(() => router.push(to))
    : router.push(to);
const openNewTeam = () => openInDrawer({ name: 'settings_teams_new' });
const { rowMenuItems, rowMenuLabel } = useSettingsRowMenu();

const searchQuery = ref('');

const teamsList = useMapGetter('teams/getTeams');

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return teamsList.value;
  return picoSearch(teamsList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const deleteTeam = async ({ id }) => {
  try {
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const tableHeaders = computed(() => [
  t('TEAMS_SETTINGS.FORM.NAME.LABEL'),
  t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL'),
  t('SETTINGS_TABLE.ACTIONS'),
]);

const onRowAction = (action, team) => {
  if (action === 'edit') {
    openInDrawer({ name: 'settings_teams_edit', params: { teamId: team.id } });
  } else {
    openDelete(team);
  }
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="!teamsList.length"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('TEAMS_SETTINGS.HEADER')"
        :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
        :link-text="$t('TEAMS_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="team_management"
      >
        <template v-if="teamsList?.length" #count>
          {{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}
        </template>
        <template v-if="isAdmin" #actions>
          <Button
            :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
            size="sm"
            @click="openNewTeam"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #drawer>
      <TeamDrawer
        v-if="isDrawerOpen"
        :key="drawerTeamId || 'new'"
        ref="drawerRef"
        :team-id="drawerTeamId"
      />
    </template>
    <template v-if="isAdmin" #emptyActions>
      <Button
        :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
        size="sm"
        outline
        @click="openNewTeam"
      />
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredTeamsList"
        :no-data-message="$t('TEAMS_SETTINGS.NO_RESULTS')"
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="team in items" :key="team.id" :item="team">
            <BaseTableCell>
              <div class="flex min-w-0 items-center gap-3">
                <span
                  class="flex size-8 shrink-0 items-center justify-center rounded-polaris-sm border border-solid border-polaris-border bg-polaris-muted"
                >
                  <EmojiIcon
                    v-if="team.icon"
                    :value="team.icon"
                    :color="team.icon_color"
                    class="size-4"
                  />
                  <Icon
                    v-else
                    icon="i-lucide-users-round"
                    class="size-4 text-polaris-muted-foreground"
                  />
                </span>
                <span class="truncate">{{ team.name }}</span>
              </div>
            </BaseTableCell>
            <BaseTableCell wrap>{{ team.description }}</BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                v-if="isAdmin"
                :label="rowMenuLabel(team.name)"
                :items="rowMenuItems()"
                @action="action => onRowAction(action, team)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
